/**
 * Firebase Configuration for Widget
 *
 * Initializes Firebase for the widget bundle.
 * Uses configuration passed via LongChat.init() or falls back to embedded config.
 */

import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, signInAnonymously, Auth, User } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let currentUser: User | null = null;

/**
 * Firebase configuration interface
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * Default Firebase config (embedded for widget)
 * These should be replaced with your actual Firebase config
 */
const DEFAULT_CONFIG: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

/**
 * Initialize Firebase for the widget
 */
export function initializeFirebase(config?: Partial<FirebaseConfig>): void {
  // Check if already initialized
  if (getApps().length > 0) {
    app = getApps()[0];
    auth = getAuth(app);
    db = getFirestore(app);
    console.log('[LongChat] Firebase already initialized');
    return;
  }

  // Merge provided config with defaults
  const finalConfig: FirebaseConfig = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  // Validate required fields
  if (!finalConfig.apiKey || !finalConfig.projectId) {
    throw new Error('[LongChat] Firebase configuration is incomplete. Please provide apiKey and projectId.');
  }

  // Initialize Firebase
  app = initializeApp(finalConfig, 'longchat-widget');
  auth = getAuth(app);
  db = getFirestore(app);

  console.log('[LongChat] Firebase initialized for widget');
}

/**
 * Sign in anonymously
 */
export async function signInAnonymouslyToFirebase(): Promise<User> {
  if (!auth) {
    throw new Error('[LongChat] Firebase not initialized. Call initializeFirebase() first.');
  }

  // Check if already signed in
  if (currentUser) {
    console.log('[LongChat] Already signed in:', currentUser.uid);
    return currentUser;
  }

  try {
    const result = await signInAnonymously(auth);
    currentUser = result.user;
    console.log('[LongChat] Signed in anonymously:', currentUser.uid);
    return currentUser;
  } catch (error) {
    console.error('[LongChat] Anonymous sign-in failed:', error);
    throw error;
  }
}

/**
 * Get the Firestore instance
 */
export function getDb(): Firestore {
  if (!db) {
    throw new Error('[LongChat] Firebase not initialized. Call initializeFirebase() first.');
  }
  return db;
}

/**
 * Get the Auth instance
 */
export function getAuthInstance(): Auth {
  if (!auth) {
    throw new Error('[LongChat] Firebase not initialized. Call initializeFirebase() first.');
  }
  return auth;
}

/**
 * Get the current user
 */
export function getCurrentUser(): User | null {
  return currentUser;
}

/**
 * Check if Firebase is initialized
 */
export function isFirebaseInitialized(): boolean {
  return app !== null;
}

